import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../context/Provider';
import SolutionFilters from '../SolutionFilters';
import Solution from '../Solution';
import { SET_SELECTED_FILTERS, SET_SELECTED_TAB } from '../../context/actions';
import { DEFAULT_FILTERS, LABELS } from '../../config/data';
import { getAppSelectionsFromUrlParams, updatePageUrl } from '../../utility/url';
import { sendUserInteractionsToTelemetryFor } from '../../utility/analytics.telemetry';
import { INITIAL_LOAD_EVENT, VIEWED_BY_USER_EVENT } from '../../config/analytics.telemetry';
import { APP_VIEWED_MINIMUM_TIME_PERIOD } from '../../config/app';
import { EVALKITS_TAB, SAMPLES_TAB, SCHEMATIC_TAB } from '../../config/tabbed-content';
import './style.css';

/**
 * Renders this app's top level container element
 * @returns {React.ReactComponentElement}
 */
const Container = () => {
	const classes = ['adi-isp'];
	const containerRef = useRef(null);
	const { globalState, dispatch } = useContext(AppContext);
	const { selectedFilters, selectedTab } = globalState;
	const { application, sensorType, optimization, solutionId } = selectedFilters;
	const areParamsPresent = window.location.search !== '';
	const { title } = LABELS;
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const areFiltersSelected = Object.keys(selectedFilters).length > 0;

	/**
	 * Initializes the selected solution filters and selected tab from url params, if present.
	 * If not present, then default them to first items of each filter, and selected tab to 'Schematic'
	 */
	const initAppSelections = useCallback(() => {
		let { selectedFilters, selectedTab } = getAppSelectionsFromUrlParams();
		const areFiltersNotSelected = selectedFilters === null;
		const isTabNotSelected = selectedTab === null;
		const isSelectedTabInvalid = ![SCHEMATIC_TAB, EVALKITS_TAB, SAMPLES_TAB].includes(
			selectedTab,
		);

		if (areFiltersNotSelected) selectedFilters = DEFAULT_FILTERS;

		if (isTabNotSelected || isSelectedTabInvalid) selectedTab = SCHEMATIC_TAB;

		dispatch({ type: SET_SELECTED_FILTERS, payload: selectedFilters });
		dispatch({ type: SET_SELECTED_TAB, payload: selectedTab });
	}, [dispatch]);

	/**
	 * Create intersection observer to check if atleast 65% of the app is being viewed by user for
	 * atleast 2 seconds (APP_VIEWED_MINIMUM_TIME_PERIOD)
	 * @param {string} solutionId Id of the solution selected (needed for telemetry request)
	 */
	const checkIfAppIsViewedByUser = useCallback(solutionId => {
		const appContainer = containerRef.current;
		const observerOptions = { threshold: 0.65 }; // Should intersect for atleast 65% of the app
		let timeoutId;

		const appViewedObserver = new IntersectionObserver(entries => {
			const app = entries[0]; // We're only observing appContainer

			if (app.isIntersecting) {
				timeoutId = setTimeout(() => {
					sendUserInteractionsToTelemetryFor(VIEWED_BY_USER_EVENT, {}, solutionId);
				}, APP_VIEWED_MINIMUM_TIME_PERIOD);
			} else {
				if (timeoutId) clearTimeout(timeoutId);
			}
		}, observerOptions);

		if (appContainer) appViewedObserver.observe(appContainer);
	}, []);

	// Some initialization operations below
	useEffect(() => {
		initAppSelections();
	}, [initAppSelections]);

	useEffect(() => {
		if (isInitialLoad && solutionId) {
			sendUserInteractionsToTelemetryFor(INITIAL_LOAD_EVENT, {}, solutionId);
			checkIfAppIsViewedByUser();
			setIsInitialLoad(false);
		}
	}, [isInitialLoad, solutionId, checkIfAppIsViewedByUser]);

	// Keep page url params in sync with selectedFilters and selectedTab at all times
	// EXCEPT during initial load and when no url params are present
	useEffect(() => {
		if (isInitialLoad && !areParamsPresent) return;

		updatePageUrl({ application, sensorType, optimization, solutionId }, selectedTab);

		/**
		 * DO NOT add `isInitialLoad` in dependency array below. This is intentional.
		 * Doing so defeats the purpose of using it in above if() condition.
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [areParamsPresent, application, sensorType, optimization, solutionId, selectedTab]);

	return (
		<section className="adi-isp-container">
			<div className={classes.join(' ')} ref={containerRef}>
				{/* Header */}
				<header className={classes[0] + '__title'}>
					<h2>{title}</h2>
				</header>

				{areFiltersSelected ? (
					<>
						<SolutionFilters />
						<Solution />
					</>
				) : null}
			</div>
		</section>
	);
};

export default Container;
